import type { ComponentType, KeyboardEvent, SVGProps } from 'react';
import { cn } from '../../../lib/utils.ts';

export type EditorTab = {
    id: string;
    label: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
};

type EditorTabsProps = {
    tabs: EditorTab[];
    activeId: string;
    onChange: (id: string) => void;
};

export function tabPanelId(id: string): string {
    return `editor-tabpanel-${id}`;
}

export function EditorTabs({ tabs, activeId, onChange }: EditorTabsProps) {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const index = tabs.findIndex((tab) => tab.id === activeId);
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        else return;
        event.preventDefault();
        onChange(tabs[next].id);
        event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
    };

    return (
        <div role="tablist" aria-label="Documentweergave" onKeyDown={handleKeyDown} className="flex items-center gap-1 border-b border-gray-200 px-3 py-2">
            {tabs.map((tab) => {
                const active = tab.id === activeId;
                const Icon = tab.icon;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        id={`editor-tab-${tab.id}`}
                        aria-selected={active}
                        aria-controls={tabPanelId(tab.id)}
                        tabIndex={active ? 0 : -1}
                        onClick={() => onChange(tab.id)}
                        className={cn(
                            'group inline-flex items-center gap-2 rounded-md py-1.5 pl-2 pr-3 text-[13px] font-semibold transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                            active
                                ? 'bg-gray-100 text-primary'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700',
                        )}
                    >
                        <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-primary' : 'text-gray-400 group-hover:text-primary')} aria-hidden="true" />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}
